/**
 * PrizeDrawStyleStandard section renderers for GridEngine
 */
import React from 'react'
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import HeroPrizeSpotlight from '../../../components/sections/HeroPrizeSpotlight'
import { getHeroPrize } from '../../../utils/prizeHelpers'

/**
 * Hero Section - Blue gradient with charity logo, title/subtitle, hero prize spotlight
 */
export function PrizeDrawStandardHeroSection({ 
  mainDraw, 
  partner, 
  editMode, 
  handleTranslationUpdate, 
  onLoginClick,
  prizes 
}) {
  const { c } = useContent()
  const heroPrize = getHeroPrize(prizes || mainDraw?.prizes || [])

  return (
    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className={heroPrize ? 'md:grid md:grid-cols-2 md:items-center md:gap-10 md:text-left' : 'text-center'}>
          {/* Hero Content */}
          <div>
            {partner?.partner_logo_url && (
              <div className={`flex mb-6 ${heroPrize ? 'justify-center md:justify-start' : 'justify-center'}`}>
                <img
                  src={partner.partner_logo_url}
                  alt={partner.partner_name || 'Charity Logo'}
                  className="h-20 w-auto object-contain bg-white rounded-lg p-2"
                />
              </div>
            )}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <InlineTranslationEdit
                translationKey="prizeDrawStandard.title"
                value={c('prizeDrawStandard.title', mainDraw?.title || 'Prize Draw')}
                fallback="Prize Draw"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>
            <p className={`text-lg text-purple-100 ${heroPrize ? '' : 'max-w-2xl mx-auto'}`}>
              <InlineTranslationEdit
                translationKey="prizeDrawStandard.subtitle"
                value={c('prizeDrawStandard.subtitle', 'Enter for a chance to win amazing prizes while supporting a great cause!')}
                fallback="Enter for a chance to win amazing prizes while supporting a great cause!"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>
            <HeroPartnerGlassSelect className={`mt-6 w-full max-w-md ${heroPrize ? 'mx-auto md:mx-0' : 'mx-auto'}`} />
          </div>

          {/* Hero Prize Spotlight */}
          {heroPrize && (
            <div className="mt-8 md:mt-0">
              <HeroPrizeSpotlight prize={heroPrize} theme="standard" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Live Draw Section - Draw date display with countdown timer
 */
export function PrizeDrawStandardLiveDrawSection({ 
  mainDraw, 
  editMode, 
  handleTranslationUpdate,
  timeLeft,
  shouldAnimate,
  countdownStyle,
  prizes
}) {
  const { c } = useContent()
  
  const grandPrizeTier = prizes?.find(p => p.tier === 'grand')
  const grandPrize = grandPrizeTier?.value ?? mainDraw?.current_jackpot ?? 10000
  const ticketsSold = mainDraw?.total_tickets_sold

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : ''

  return (
    <>
      {/* Grand Prize Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wide">
            <InlineTranslationEdit
              translationKey="prizeDrawStandard.grandPrize"
              value={c('prizeDrawStandard.grandPrize', 'Grand Prize')}
              fallback="Grand Prize"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h2>
        </div>
        <div className="text-4xl md:text-5xl font-black text-purple-600 dark:text-purple-400 mb-2">
          ${grandPrize.toLocaleString()}
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          <InlineTranslationEdit
            translationKey="prizeDrawStandard.grandPrizeDesc"
            value={c('prizeDrawStandard.grandPrizeDesc', grandPrizeTier?.description || mainDraw?.prize_description || 'One lucky winner takes home the grand prize!')}
            fallback={grandPrizeTier?.description || mainDraw?.prize_description || 'One lucky winner takes home the grand prize!'}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Tickets Sold */}
        {ticketsSold != null && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-2 mb-3">
              <Ticket className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                <InlineTranslationEdit
                  translationKey="prizeDrawStandard.ticketsSold"
                  value={c('prizeDrawStandard.ticketsSold', 'Tickets Sold')}
                  fallback="Tickets Sold"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h3>
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {ticketsSold.toLocaleString()}
            </div>
          </div>
        )}

        {/* Draw Date */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              <InlineTranslationEdit
                translationKey="prizeDrawStandard.drawDate"
                value={c('prizeDrawStandard.drawDate', 'Draw Date')}
                fallback="Draw Date"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {drawDateLabel || 'To be announced'}
          </div>
        </div>
      </div>

      {/* Countdown Timer */}
      {mainDraw?.draw_date && timeLeft && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              <InlineTranslationEdit
                translationKey="prizeDrawStandard.countdown"
                value={c('prizeDrawStandard.countdown', 'Time Until Draw')}
                fallback="Time Until Draw"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
          </div>
          <CountdownTimerStyles
            timeLeft={timeLeft}
            shouldAnimate={shouldAnimate}
            style={countdownStyle}
          />
        </div>
      )}
    </>
  )
}

/**
 * How It Works Section - 4-step cards
 */
export function PrizeDrawStandardHowItWorksSection({ editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const howItWorksSteps = [
    { icon: Ticket, titleKey: 'prizeDrawStandard.step1Title', titleDefault: 'Buy Tickets', descKey: 'prizeDrawStandard.step1Desc', descDefault: 'Purchase your tickets online' },
    { icon: Calendar, titleKey: 'prizeDrawStandard.step2Title', titleDefault: 'Wait for Draw', descKey: 'prizeDrawStandard.step2Desc', descDefault: 'Entries close before the draw date' },
    { icon: Gift, titleKey: 'prizeDrawStandard.step3Title', titleDefault: 'Win Prizes', descKey: 'prizeDrawStandard.step3Desc', descDefault: 'Winners are randomly selected' },
    { icon: Gift, titleKey: 'prizeDrawStandard.step4Title', titleDefault: 'Claim Your Prize', descKey: 'prizeDrawStandard.step4Desc', descDefault: 'Winners are contacted directly' }
  ]

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          <InlineTranslationEdit
            translationKey="prizeDrawStandard.howItWorks"
            value={c('prizeDrawStandard.howItWorks', 'How It Works')}
            fallback="How It Works"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {howItWorksSteps.map((step, index) => {
          const Icon = step.icon
          return (
            <div key={index} className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                <InlineTranslationEdit
                  translationKey={step.titleKey}
                  value={c(step.titleKey, step.titleDefault)}
                  fallback={step.titleDefault}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit
                  translationKey={step.descKey}
                  value={c(step.descKey, step.descDefault)}
                  fallback={step.descDefault}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
